import { DateRangeFilter } from '@components/_SiteWide/Grid/ActionButtons/DateRangeFilter'
import { DownloadButton } from '@components/_SiteWide/Grid/ActionButtons/DownloadButton'
import type {
  Filters,
  ForecastManagementRow,
  ForecastManagementRowChange,
  OverrideUpsertRequest,
} from '@components/StoreMonitoring/ForecastManagement/api/schema'
import { useForecastManagement } from '@components/StoreMonitoring/ForecastManagement/api/useForecastManagement'
import AssociateEventModal from '@components/StoreMonitoring/ForecastManagement/components/AssociateEventModal'
import { ForecastBulkChangeBar } from '@components/StoreMonitoring/ForecastManagement/components/Grid/ForecastBulkChangeBar'
import { GraviGrid, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useLocalStorage } from '@gravitate-js/the-armory'
import { useUserSettings } from '@hooks/settings'
import { useQueryClient } from '@tanstack/react-query'
import type { GridApi, RangeSelectionChangedEvent } from 'ag-grid-community'
import { Switch, Tooltip } from 'antd'
import moment from 'moment-timezone'
import type React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { DEFAULT_FILTER_OPTIONS } from '../../constants'
import { getColumnDefs } from './columnDefs'
import { useWebSocketStore } from '@hooks/websocket/useWebsocket'
import { gridViewManager } from '@/utils/grid'

function hasNoFilters(filters: Filters) {
  return Object.values(filters).every((filter) => (Array.isArray(filter) ? filter.length === 0 : !filter))
}

const getSelectedCellMap = (selectedCells: any[]): Map<number, string[]> => {
  const result = new Map<number, string[]>()

  selectedCells.forEach((range) => {
    const { startRow, endRow, columns } = range

    // Ensure startRowIndex is less than or equal to endRowIndex
    const startRowIndex = Math.min(startRow.rowIndex, endRow.rowIndex)
    const endRowIndex = Math.max(startRow.rowIndex, endRow.rowIndex)

    for (let i = startRowIndex; i <= endRowIndex; i++) {
      if (!result.has(i)) {
        result.set(i, [])
      }

      const existingColumns = result.get(i)!

      columns.forEach((col) => {
        if (!existingColumns.includes(col.colId)) {
          existingColumns.push(col.colId)
        }
      })

      result.set(i, existingColumns) // Update the map with the new array
    }
  })

  return result
}

const formatCellInformation = (cellMap: Map<number, string[]>, gridAPI: GridApi): OverrideUpsertRequest[] =>
  // Extracts all the relevant data from the grid based on selected cells so we can manipulate the data.
  Array.from(cellMap).flatMap(([rowIndex, columns]) => {
    const data = gridAPI.getDisplayedRowAtIndex(rowIndex)?.data as ForecastManagementRow
    if (!data) return []
    // filter out the columns that don't start with volume_
    const filteredColumns = columns
      .filter((colId) => colId.startsWith('volume_'))
      .map((colId) => {
        const forecastIndex = parseInt(colId.split('_')[1])
        return {
          forecast_index: forecastIndex,
          id: data.id,
          col_id: colId,
          store_number: data.store_number,
          tank_id: data.tank_id,
          date: data.days[forecastIndex].date,
          original_volume: data.days[forecastIndex].original_volume,
          volume: data.days[forecastIndex].volume,
        }
      })
    return filteredColumns
  })

const STORAGE_KEY = 'forecast-management'

export function MassForecastGrid() {
  const { updateSetting } = useUserSettings()
  const gridAPIRef: React.MutableRefObject<GridApi | null> = useRef(null)
  const columnApiRef = useRef(null)
  const dirtyRef = useRef<any>(null)

  const { useForecastManagementOverview, useBulkForecastOverride, useForecastManagementMetadata } =
    useForecastManagement()
  const { forecastOverrideStatus } = useWebSocketStore((state) => state())

  const [selectedCells, setSelectedCells] = useState([])
  const queryClient = useQueryClient()

  const [showAssociateEventModal, setShowAssociateEventModal] = useState(false)
  const [payload, setPayload] = useState(null)
  const [dirtyChangesToRemove, setDirtyChangesToRemove] = useState([])

  const { getSetting } = useUserSettings()
  const [baseDefaultChecked, setBaseChecked] = useState(false)

  const { value: forecastDayRange, setValue: setOMDayRange } = useLocalStorage('forecast-management-od-range', {
    back: 1,
    ahead: 3,
  })

  const [dateRange, setDateRange] = useState({
    From: forecastDayRange.back
      ? moment().startOf('day').add(forecastDayRange.back, 'day')
      : moment().startOf('day').add(1, 'day'),
    To: forecastDayRange.ahead
      ? moment().startOf('day').add(forecastDayRange.ahead, 'day')
      : moment().startOf('day').add(3, 'day'),
  })

  const setDayRange = async (value) => {
    // For some reason the value can be in different formats from the date picker
    if (value?.back && value?.ahead) {
      setOMDayRange({
        back: value.back,
        ahead: value.ahead,
      })
      return
    }
    const back = value.From.diff(moment().startOf('day'), 'days')
    const ahead = value.To.diff(moment().startOf('day'), 'days')
    setOMDayRange({
      back,
      ahead: ahead - back > 6 ? back + 6 : ahead,
    })
    await updateSetting('forecast-management-od-range', value)
  }

  const getDatesFromStorage = () => {
    if (forecastDayRange) {
      return {
        From: moment().add(forecastDayRange.back, 'days').endOf('day'),
        To: moment().add(forecastDayRange.ahead, 'days').endOf('day'),
      }
    }
    return null
  }

  const { value: filters, setValue: setFilters } = useLocalStorage(
    'forecast_management_server_query_filters',
    DEFAULT_FILTER_OPTIONS
  )

  const toggleBaseColumns = (show: boolean) => {
    if (columnApiRef.current) {
      const baseColumns = columnApiRef.current
        .getAllColumns()
        ?.filter((col) => col.colId.startsWith('original_volume_'))
        .map((col) => col.colId)
      if (baseColumns) {
        columnApiRef.current.setColumnsVisible(baseColumns, show)
      }
    }
  }

  const defaultBaseSwitchState = async () => {
    const showBase = await getSetting('forecast-management-show-base')
    setBaseChecked(showBase)
    toggleBaseColumns(showBase)
  }

  // Metadata
  const { data: metadata } = useForecastManagementMetadata(dateRange.From, dateRange.To)

  // Row Data
  const { data, isFetching, refetch } = useForecastManagementOverview(dateRange.From, dateRange.To, filters)

  useEffect(() => {
    defaultBaseSwitchState()
    // Retains dirty changes when adjusting filters
    if (!isFetching && data?.rows && !dirtyChangesToRemove.length) {
      gridAPIRef.current.applyTransactionAsync({ update: dirtyRef.current?.dirtyChanges })
    }
  }, [isFetching])

  const [isExcludedFromForecast, setExcludeFromForecast] = useState(false)

  const columnDefs = useMemo(
    () => getColumnDefs(data?.headers ?? [], forecastOverrideStatus, dirtyRef, isExcludedFromForecast),
    [data, forecastOverrideStatus, isExcludedFromForecast]
  )

  const isLockedDown = useMemo(async () => {
    if (!data?.rows) return false
    if (forecastOverrideStatus === undefined) return false

    // The following lines prevent dirty changes from getting lost if another user makes changes
    const dirtyChanges = dirtyRef.current?.dirtyChanges
    const store_tanks = Object.keys(forecastOverrideStatus?.progress ?? {})
    if (store_tanks.length === 0) {
      await refetch()
      if (dirtyChangesToRemove.length) {
        const newDirtyChanges = dirtyChanges.filter((change: ForecastManagementRow) => {
          return !dirtyChangesToRemove.includes(change.id)
        })
        dirtyRef.current.setDirtyChanges(newDirtyChanges)
        gridAPIRef.current.applyTransactionAsync({ update: newDirtyChanges }, () => {
          setDirtyChangesToRemove([])
        })
      }
      return true
    }
    const uniqueStoreTanks = new Map(dirtyChangesToRemove.map((id) => [id, true]))
    store_tanks.forEach((store_tank) => uniqueStoreTanks.set(store_tank.replace('|', '_'), true))
    setDirtyChangesToRemove(Array.from(uniqueStoreTanks.keys()))
    return false
  }, [forecastOverrideStatus])

  // Handlers
  const handleSave = async ({ dirtyChanges }) => {
    setSelectedCells([])
    queryClient.invalidateQueries({ queryKey: ['eventsMetadata'], refetchType: 'all' })
    const uniqueChanges = Array.from(new Map(dirtyChanges.map((item) => [item.id, item])).values())
    const payload = []
    let delete_only = true
    for (const [_, change] of Object.entries(uniqueChanges as ForecastManagementRowChange[])) {
      for (const day of change.days) {
        if (!day.is_dirty_edited) continue
        if (day.original_volume === null && !day.delete_override && !day.is_sales_excluded) continue
        payload.push({
          store_number: change.store_number,
          tank_id: change.tank_id,
          date: day.date,
          override_volume: parseFloat(day.volume),
          original_volume: day.original_volume,
          exclude_from_forecast: day.is_sales_excluded && !day.delete_override,
          exclude_forecast_from_sales: day.is_sales_excluded && !day.delete_override,
          delete_override: day.delete_override,
          event_id: day.event_id,
        })
        delete_only = !!day.delete_override && delete_only
      }
    }
    setPayload(payload)
    if (!delete_only) {
      setShowAssociateEventModal(true)
    } else {
      await useBulkForecastOverride.mutateAsync(payload)
    }
  }

  const handleBulkOverride = async (payload: OverrideUpsertRequest[]) => {
    try {
      await useBulkForecastOverride.mutateAsync(payload)
      dirtyRef.current.setDirtyChanges([])
    } catch (error) {
      console.error(error)
      return
    }
    setShowAssociateEventModal(false)
  }

  const handleAssociateSave = async (eventId?: string, eventName?: string, eventType?: string) => {
    const newPayload = payload.map((item) => ({
      ...item,
      event_id: eventId,
      event_name: eventName,
      event_type: eventType,
    }))
    handleBulkOverride(newPayload)
  }

  const handleAssociateSkip = async () => handleBulkOverride(payload)

  const getSelectedCells = useCallback((event: RangeSelectionChangedEvent) => {
    const gridAPI = gridAPIRef.current
    if (!gridAPI) return []
    const cells = gridAPI.getCellRanges()
    const map = getSelectedCellMap(cells)
    const selectedCells = formatCellInformation(map, gridAPI)
    setSelectedCells(selectedCells)
  }, [])

  const agPropOverrides = useMemo(() => {
    return {
      onRangeSelectionChanged: getSelectedCells,
      getRowId: (row) => row.data?.id,
      groupDisplayType: 'multipleColumns',
      suppressAggFuncInHeader: true,
    }
  }, [])

  const controlBarProps = useMemo(() => {
    return {
      title: 'Forecast Management',
      serverParams: metadata?.params,
      filters,
      setFilters,
      warning: data?.warning,
      maxFilterTagsArrayLength: 1,
      filterDrawerDefaultExpanded: hasNoFilters(filters),
      actionButtons: (
        <Horizontal verticalCenter style={{ gap: '1em' }}>
          <Horizontal verticalCenter style={{ minWidth: 'fit-content' }}>
            <Texto appearance='primary' weight='bold'>
              Click Forecast Cells to Override
            </Texto>
          </Horizontal>

          <Horizontal verticalCenter style={{ overflow: 'visible' }}>
            <Tooltip title='Show/Hides All Base Columns'>
              <span className='mr-2'>Base</span>
            </Tooltip>
            <Switch
              checked={baseDefaultChecked}
              size='small'
              onChange={async (checked) => {
                await updateSetting('forecast-management-show-base', checked)
                setBaseChecked(checked)
                toggleBaseColumns(checked)
              }}
              className='mr-0'
            />
          </Horizontal>
          <DateRangeFilter
            currentFilters={dateRange}
            setCurrentFilters={setDateRange}
            getFromStorage={getDatesFromStorage}
            dayRangeStorage={forecastDayRange}
            setDayRangeStorage={setDayRange}
            min={1}
            max={90}
            maxDateRange={7}
            minDate={moment().startOf('day').add(1, 'day').toDate()}
          />
          <DownloadButton fileName='Forecast Management' gridAPIRef={gridAPIRef} />
        </Horizontal>
      ),
    }
  }, [isLockedDown, forecastOverrideStatus, baseDefaultChecked, metadata?.params, data?.rows])

  const filteredSelectedCells = selectedCells?.filter((cell) => {
    const lkp = `${cell.store_number}|${cell.tank_id}`
    return !forecastOverrideStatus?.store_tanks?.includes(lkp)
  })

  return (
    <Vertical>
      <GraviGrid
        columnDefs={columnDefs}
        agPropOverrides={agPropOverrides}
        controlBarProps={controlBarProps}
        primaryKey='id'
        storageKey={STORAGE_KEY}
        onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
        loading={isFetching}
        rowData={data?.rows || []}
        bulkDrawerTitle='Bulk Edit Forecast'
        externalRef={gridAPIRef}
        columnApiRef={columnApiRef}
        dirtyChangesRef={dirtyRef}
        onDirtyChangeSave={handleSave}
        onDirtyChangeDiscard={async () => {
          // refetch to prevent grid from discarding back to stale data i.e. after another user makes changes
          const res = await refetch()
          await gridAPIRef.current.applyTransactionAsync({ update: res.data.rows })
        }}
        overlayNoRowsTemplate={hasNoFilters(filters) ? 'Apply Filter to View Rows' : 'No Rows Found'}
        isDirtyEdit
        hideSaveDisplay
        {...gridViewManager(STORAGE_KEY, updateSetting)}
      />
      {filteredSelectedCells?.length > 0 && (
        <ForecastBulkChangeBar
          selectedCells={filteredSelectedCells}
          setSelectedCells={setSelectedCells}
          gridAPIRef={gridAPIRef}
          dirtyRef={dirtyRef}
          isExcludedFromForecast={isExcludedFromForecast}
          setExcludeFromForecast={setExcludeFromForecast}
        />
      )}
      <AssociateEventModal
        visible={showAssociateEventModal}
        onSave={handleAssociateSave}
        onSkip={handleAssociateSkip}
        onCancel={() => setShowAssociateEventModal(false)}
      />
    </Vertical>
  )
}
